"use client"

import React, {useCallback, useEffect, useMemo, useState} from "react"
import Image from "next/image"
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion"
import {ShopDto} from "@/openapi/types"
import { Link } from "@/i18n/navigation"
import { usePopularShops } from "@/app/admin/shops/_api/use-shop-queries"
import { Skeleton } from "@/components/ui/skeleton"
import { useTranslations } from "next-intl"

interface ShopColumnProps {
    // shopsPromise: Promise<ShopDto[]>
    shops: ShopDto[]
    index: number
    currentTime: number
}

/**
 * Shuffles an array randomly using Fisher-Yates shuffle algorithm
 * @template T - The type of array elements
 * @param array - The array to shuffle
 * @returns A new shuffled array
 */
const shuffleArray = <T, >(array: T[]): T[] => {
    if (!array || array.length === 0) return []
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const temp = shuffled[i]
        const swapItem = shuffled[j]
        if (temp !== undefined && swapItem !== undefined) {
            shuffled[i] = swapItem
            shuffled[j] = temp
        }
    }
    return shuffled
}

/**
 * Distributes shops evenly across the specified number of columns
 * @param allShops - Array of all available shops
 * @param columnCount - Number of desired columns
 * @returns 2D array with shops per column
 */
const distributeShops = (allShops: ShopDto[], columnCount: number): ShopDto[][] => {
    if (!allShops || allShops.length === 0) return Array.from({length: columnCount}, () => [])

    const shuffled = shuffleArray(allShops)
    const columns: ShopDto[][] = Array.from({length: columnCount}, () => [])

    shuffled.forEach((shop, index) => {
        if (shop) {
            const columnIndex = index % columnCount
            const targetColumn = columns[columnIndex]
            if (targetColumn) {
                targetColumn.push(shop)
            }
        }
    })

    const columnLengths = columns.map((col) => col.length)
    const maxLength = columnLengths.length > 0 ? Math.max(...columnLengths) : 0

    if (maxLength === 0) return columns

    columns.forEach((col) => {
        if (col && shuffled.length > 0) {
            while (col.length < maxLength) {
                const randomIndex = Math.floor(Math.random() * shuffled.length)
                const randomShop = shuffled[randomIndex]
                if (randomShop) {
                    col.push(randomShop)
                }
            }
        }
    })

    return columns
}

/**
 * Individual column of the shop carousel with animated shop logos
 * Displays different shops sequentially with transition animations
 */
const ShopColumn: React.FC<ShopColumnProps> = React.memo(({shops, index, currentTime}) => {
    if (!shops || shops.length === 0) return null

    const cycleInterval = 2000
    const columnDelay = index * 200
    const adjustedTime = (currentTime + columnDelay) % (cycleInterval * shops.length)
    const currentIndex = Math.floor(adjustedTime / cycleInterval)

    // Ensure currentIndex is within bounds and shop exists
    const safeIndex = Math.max(0, Math.min(currentIndex, shops.length - 1))
    const currentShop = shops[safeIndex]

    if (!currentShop) return null

    return (
        <LazyMotion features={domAnimation}>
        <m.div
            className="relative h-14 w-24 overflow-hidden md:h-24 md:w-48"
            initial={{opacity: 0, y: 50}}
            animate={{opacity: 1, y: 0}}
            transition={{
                delay: index * 0.1,
                duration: 0.5,
                ease: "easeOut",
            }}
        >
            <AnimatePresence mode="wait">
                <m.div
                    key={`${currentShop.id}-${safeIndex}`}
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{y: "10%", opacity: 0, filter: "blur(8px)"}}
                    animate={{
                        y: "0%",
                        opacity: 1,
                        filter: "blur(0px)",
                        transition: {
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            mass: 1,
                            bounce: 0.2,
                            duration: 0.5,
                        },
                    }}
                    exit={{
                        y: "-20%",
                        opacity: 0,
                        filter: "blur(6px)",
                        transition: {
                            type: "tween",
                            ease: "easeIn",
                            duration: 0.3,
                        },
                    }}
                >
                    <div className="relative h-20 w-20 max-h-[80%] max-w-[80%] md:h-32 md:w-32">
                        <Link href={`/shops`} passHref>
                            <div className="relative h-20 w-20 max-h-[80%] max-w-[80%] md:h-32 md:w-32 cursor-pointer">
                                <Image
                                    src={currentShop.logoUrl || "/placeholder.svg"}
                                    alt={`${currentShop.name} Logo`}
                                    fill
                                    className="object-contain"
                                    sizes="(max-width: 768px) 80px, 128px"
                                />
                            </div>
                        </Link>

                    </div>
                </m.div>
            </AnimatePresence>
        </m.div>
        </LazyMotion>
    )
})

ShopColumn.displayName = "ShopColumn"

/**
 * Props for the ShopCarousel component
 */
interface ShopCarouselProps {
    columnCount?: number
    size: number;
}

/**
 * Animated shop carousel that displays popular shops in multiple columns
 * Each column shows different shop logos sequentially with smooth transition animations
 *
 * @param columnCount - Number of columns (default: 2)
 * @param size - Number of shops to fetch
 */
export function ShopCarousel({columnCount = 2, size}: ShopCarouselProps) {
    const t = useTranslations("common")
    const { data: shopsPage, isLoading, error } = usePopularShops({ size });
    const shops = shopsPage?.content;
    const shopSets = useMemo<ShopDto[][]>(() => {
        if (shops && shops.length > 0) return distributeShops(shops, columnCount)
        return []
    }, [shops, columnCount])
    const [currentTime, setCurrentTime] = useState(0)

    const updateTime = useCallback(() => {
        setCurrentTime((prevTime) => prevTime + 100)
    }, [])

    useEffect(() => {
        const intervalId = setInterval(updateTime, 100)
        return () => clearInterval(intervalId)
    }, [updateTime])

    if (isLoading) {
        return (
            <div className="flex space-x-4">
                {Array.from({ length: columnCount }).map((_, index) => (
                    <div key={index} className="relative h-14 w-24 overflow-hidden md:h-24 md:w-48">
                        <Skeleton className="h-full w-full" />
                    </div>
                ))}
            </div>
        )
    }

    if (error) {
        return <div className="text-center text-muted-foreground">{t("shopCarousel.loadError")}</div>
    }

    if (!shops || shops.length === 0) {
        return <div className="text-center text-muted-foreground">{t("shopCarousel.noShops")}</div>
    }

    return (
        <div className="flex space-x-4">
            {shopSets.map((shops, index) => (
                <ShopColumn key={index} shops={shops} index={index} currentTime={currentTime}/>
            ))}
        </div>
    )
}

export {ShopColumn}
